import { parseISO } from "date-fns";
import type { MetricTableBlockRow, TableBlockColumn } from "../../../../api/inputFormTypes";
import { formatDateOnly } from "../../../../utilities/dateUtils";
import { convertToNumber } from "../../../../utilities/numberHelper";
import { stringifyValue } from "../../../../utilities/stringHelper";
import MetricDateField from "../fields/MetricDateField";
import MetricMoneyField from "../fields/MetricMoneyField";
import MetricNumberField from "../fields/MetricNumberField";
import MetricSelectField from "../fields/MetricSelectField";
import MetricTextField from "../fields/MetricTextField";
import type { TrafficLightValue } from "../fields/TrafficLightSelectField";
import TrafficLightSelectField from "../fields/TrafficLightSelectField";
import useDataSubmissionFormContext from "../useDataSubmissionFormContext";

interface Props {
  row: MetricTableBlockRow;
  columnDefinition: TableBlockColumn;
  currencyCode: string;
  onValueChange: (rowId: string, metricId: string, columnId: string, value: string | number | undefined) => void;
}

const trafficLightToNumberMap: Record<TrafficLightValue, number> = {
  green: 1,
  yellow: 2,
  red: 3,
};

const numberToTrafficLightMap: Record<number, TrafficLightValue> = Object.fromEntries(
  Object.entries(trafficLightToNumberMap).map(([key, value]) => [value, key as TrafficLightValue])
);

const TableFormCellEditor = ({ row, columnDefinition, currencyCode, onValueChange }: Props) => {
  const { isSubmissionEditable } = useDataSubmissionFormContext();

  const handleValueChange = (value: string | number | undefined) => {
    onValueChange(row.id, row.metricId, columnDefinition.id, value);
  };

  if (row.type !== "Metric") {
    return null;
  }

  const cellValue = row.values[columnDefinition.id];
  const readOnly = !columnDefinition.isEditable;
  const disabled = !isSubmissionEditable || readOnly;

  if (row.dataType === "Select" && row.valueSource) {
    return (
      <MetricSelectField
        value={cellValue?.toString()}
        onChange={handleValueChange}
        valueSource={row.valueSource}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  if (row.dataType === "Text") {
    return (
      <MetricTextField
        value={cellValue?.toString() ?? ""}
        onChange={handleValueChange}
        inputSettings={row.inputSettings}
        rows={4}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  if (row.dataType === "Number" && row.inputComponent === "TrafficLightSelect") {
    const numericValue = convertToNumber(cellValue);
    const trafficLightValue = numericValue ? numberToTrafficLightMap[numericValue] : undefined;
    return (
      <TrafficLightSelectField
        value={trafficLightValue}
        onChange={(value) => handleValueChange(trafficLightToNumberMap[value])}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  if (row.dataType === "Number") {
    const numericValue = convertToNumber(cellValue);
    return (
      <MetricNumberField
        value={numericValue}
        onChange={handleValueChange}
        formatConfig={row.defaultFormat}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  if (row.dataType === "Money") {
    const numericValue = convertToNumber(cellValue);
    return (
      <MetricMoneyField
        value={numericValue}
        onChange={handleValueChange}
        formatConfig={row.defaultFormat}
        currencyCode={currencyCode}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  if (row.dataType === "Date") {
    const dateValue = typeof cellValue === "string" ? parseISO(cellValue) : undefined;
    return (
      <MetricDateField
        value={dateValue}
        onChange={(newDate) => handleValueChange(newDate ? formatDateOnly(newDate) : undefined)}
        readOnly={readOnly}
        disabled={disabled}
      />
    );
  }

  return (
    <MetricTextField
      value={stringifyValue(cellValue)}
      onChange={handleValueChange}
      inputSettings={row.inputSettings}
      readOnly={readOnly}
      disabled={disabled}
    />
  );
};

export default TableFormCellEditor;
